import React, { useState, useEffect } from 'react';
import { useOutletContext, Link, useLocation } from 'react-router-dom';
import { 
  Building2, 
  User, 
  Users, 
  Settings as SettingsIcon,
  Globe,
  Mail,
  Image,
  FileText,
  Save,
  Upload,
  Check,
  AlertCircle,
  ChevronRight,
  Palette,
  Camera
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import '../../styles/CuratorSettings.css';

// Helper to add cloudinary transform
const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', `/upload/${transform}`);
};

const CuratorSettings = () => {
  const context = useOutletContext();
  const location = useLocation();
  const { selectedOrganization, user } = context || {};
  
  // Determine active tab from URL hash or default to 'organization'
  const hash = location.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(hash || 'organization');
  
  // Organization form state
  const [orgForm, setOrgForm] = useState({
    name: '',
    description: '',
    website: '',
    contact_email: '',
    billing_email: '',
    logo: '',
    cover_image: ''
  });
  
  // Personal profile form state
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    profile_img: '',
    handle: '',
    instagram: '',
    phone: '',
    mobile_phone: '',
    cover_image: ''
  });
  
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(null); // Track which image is uploading

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load organization data if we have a selected org
        if (selectedOrganization?.organization_id) {
          // Get full org details
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('*')
            .eq('organization_id', selectedOrganization.organization_id)
            .single();
          
          if (!orgError && orgData) {
            setOrgForm({
              name: orgData.name || '',
              description: orgData.description || '',
              website: orgData.website || '',
              contact_email: orgData.contact_email || '',
              billing_email: orgData.billing_email || '',
              logo: orgData.logo || '',
              cover_image: orgData.cover_image || ''
            });
          }
          
          // Get current user's role in this org
          if (user) {
            const { data: memberData } = await supabase
              .from('organization_members')
              .select('role')
              .eq('organization_id', selectedOrganization.organization_id)
              .eq('user_uuid', user.id)
              .eq('is_active', true)
              .single();
            
            setCurrentUserRole(memberData?.role || null);
          }
          
          // Get team member count
          const { count } = await supabase
            .from('organization_members')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', selectedOrganization.organization_id)
            .eq('is_active', true);
          
          setTeamMembers(count || 0);
        }
        
        // Load personal profile
        if (user) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('uuid', user.id)
            .single();
          
          if (!userError && userData) {
            setProfileForm({
              first_name: userData.first_name || '',
              last_name: userData.last_name || '',
              bio: userData.bio || '',
              profile_img: userData.profile_img || '',
              handle: userData.handle || '',
              instagram: userData.instagram || '',
              phone: userData.phone || '',
              mobile_phone: userData.mobile_phone || '',
              cover_image: userData.cover_image || ''
            });
          }
        }
      } catch (error) {
        console.error('Error loading settings data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedOrganization, user]);

  // Check if user can edit org settings (owner or admin)
  const canEditOrg = currentUserRole === 'owner' || currentUserRole === 'admin';
  const isOwner = currentUserRole === 'owner';

  // Handle org form change
  const handleOrgChange = (field, value) => {
    setOrgForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle profile form change
  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle image upload to Cloudinary
  const handleImageUpload = async (target, file, folder = 'profiles') => {
    if (!file) return;
    
    setUploadingImage(target);
    setSaveError(null);
    
    try {
      const result = await uploadToCloudinary(file, 'image', folder);
      
      // Determine which form to update based on target
      if (target === 'org-logo' || target === 'org-cover') {
        const field = target === 'org-logo' ? 'logo' : 'cover_image';
        setOrgForm(prev => ({ ...prev, [field]: result.url }));
      } else if (target === 'profile-img' || target === 'profile-cover') {
        const field = target === 'profile-img' ? 'profile_img' : 'cover_image';
        setProfileForm(prev => ({ ...prev, [field]: result.url }));
      }
      
      setSaveSuccess('Image uploaded! Remember to save your changes.');
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error('Error uploading image:', error);
      setSaveError('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(null);
    }
  };

  // Render image upload section
  const renderImageUpload = (target, currentImage, title, isSquare = false, disabled = false) => {
    const isUploading = uploadingImage === target;
    const previewClass = isSquare ? 'image-preview avatar' : 'image-preview cover';
    
    return (
      <div className="image-upload-container">
        <label className="image-upload-label">{title}</label>
        <div className={`image-upload-box ${isSquare ? 'square' : 'cover'} ${disabled ? 'disabled' : ''}`}>
          {currentImage ? (
            <img 
              src={addCloudinaryTransform(currentImage, isSquare ? 't_square-150/' : 't_fitmax-300/')} 
              alt={title}
              className={previewClass}
            />
          ) : (
            <div className="image-placeholder">
              <Camera size={32} color="#6c757d" />
              <span>No image</span>
            </div>
          )}
          {!disabled && (
            <div className="image-upload-overlay">
              <label className="upload-trigger">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload(target, file, target.startsWith('org') ? 'organizations' : 'profiles');
                  }}
                  style={{ display: 'none' }}
                  disabled={isUploading}
                />
                <Camera size={20} color="#ffffff" />
                <span>{isUploading ? 'Uploading...' : 'Change'}</span>
              </label>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Save organization settings
  const handleSaveOrg = async () => {
    if (!canEditOrg || !selectedOrganization?.organization_id) return;
    
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);
    
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: orgForm.name,
          description: orgForm.description,
          website: orgForm.website,
          contact_email: orgForm.contact_email,
          billing_email: orgForm.billing_email,
          logo: orgForm.logo,
          cover_image: orgForm.cover_image,
          updated_at: new Date().toISOString()
        })
        .eq('organization_id', selectedOrganization.organization_id);
      
      if (error) throw error;
      
      setSaveSuccess('Organization settings saved successfully!');
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving organization:', error);
      setSaveError(error.message || 'Failed to save organization settings');
    } finally {
      setSaving(false);
    }
  };

  // Save personal profile
  const handleSaveProfile = async () => {
    if (!user) return;
    
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);
    
    try {
      const { error } = await supabase
        .from('users')
        .update({
          first_name: profileForm.first_name,
          last_name: profileForm.last_name,
          bio: profileForm.bio,
          profile_img: profileForm.profile_img,
          handle: profileForm.handle,
          instagram: profileForm.instagram,
          phone: profileForm.phone,
          mobile_phone: profileForm.mobile_phone,
          cover_image: profileForm.cover_image,
          updated_at: new Date().toISOString()
        })
        .eq('uuid', user.id);
      
      if (error) throw error;
      
      setSaveSuccess('Profile saved successfully!');
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError(error.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="curator-settings">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-settings">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your organization and personal account settings</p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'organization' ? 'active' : ''}`}
          onClick={() => setActiveTab('organization')}
        >
          <Building2 size={18} />
          Organization
        </button>
        <button
          className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          <User size={18} />
          My Account
        </button>
      </div>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <div className="settings-alert success">
          <Check size={18} />
          {saveSuccess}
        </div>
      )}
      {saveError && (
        <div className="settings-alert error">
          <AlertCircle size={18} />
          {saveError}
        </div>
      )}

      {/* Organization Settings Tab */}
      {activeTab === 'organization' && (
        <div className="settings-content">
          {!selectedOrganization ? (
            <div className="settings-empty">
              <Building2 size={48} />
              <h3>No Organization Selected</h3>
              <p>Please select an organization from the dashboard to manage its settings.</p>
              <Link to="/curator/dashboard" className="btn-primary">
                Go to Dashboard
              </Link>
            </div>
          ) : (
            <>
              {/* Organization Profile Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <h2>Organization Profile</h2>
                  {!canEditOrg && (
                    <span className="role-badge">View Only (You are {currentUserRole})</span>
                  )}
                </div>

                <div className="settings-form">
                  <div className="form-group">
                    <label>Organization Name</label>
                    <input
                      type="text"
                      value={orgForm.name}
                      onChange={(e) => handleOrgChange('name', e.target.value)}
                      disabled={!canEditOrg}
                      placeholder="Organization name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={orgForm.description}
                      onChange={(e) => handleOrgChange('description', e.target.value)}
                      disabled={!canEditOrg}
                      placeholder="Describe your organization..."
                      rows={4}
                    />
                  </div>

                  <div className="form-row">
                    {renderImageUpload('org-logo', orgForm.logo, 'Logo', true, !canEditOrg)}
                    {renderImageUpload('org-cover', orgForm.cover_image, 'Cover Image', false, !canEditOrg)}
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="settings-section">
                <h2>Contact Information</h2>
                <div className="settings-form">
                  <div className="form-group">
                    <label>
                      <Globe size={16} />
                      Website
                    </label>
                    <input
                      type="url"
                      value={orgForm.website}
                      onChange={(e) => handleOrgChange('website', e.target.value)}
                      disabled={!canEditOrg}
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <Mail size={16} />
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={orgForm.contact_email}
                        onChange={(e) => handleOrgChange('contact_email', e.target.value)}
                        disabled={!canEditOrg}
                        placeholder="contact@organization.com"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <Mail size={16} />
                        Billing Email
                      </label>
                      <input
                        type="email"
                        value={orgForm.billing_email}
                        onChange={(e) => handleOrgChange('billing_email', e.target.value)}
                        disabled={!isOwner}
                        placeholder="billing@organization.com"
                      />
                      {!isOwner && canEditOrg && (
                        <span className="field-note">Only owners can edit billing email</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Management Link */}
              <div className="settings-section">
                <h2>Team Management</h2>
                <div className="settings-link-card">
                  <div className="link-card-content">
                    <Users size={24} />
                    <div>
                      <h3>Team Members</h3>
                      <p>{teamMembers} members in this organization</p>
                    </div>
                  </div>
                  <Link 
                    to={`/curator/organization/${selectedOrganization.slug}/team`}
                    className="link-card-action"
                  >
                    Manage Team <ChevronRight size={18} />
                  </Link>
                </div>
              </div>

              {/* Save Button */}
              {canEditOrg && (
                <div className="settings-actions">
                  <button 
                    className="btn-primary"
                    onClick={handleSaveOrg}
                    disabled={saving}
                  >
                    {saving ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <Save size={18} />
                        Save Organization Settings
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Personal Account Tab */}
      {activeTab === 'account' && (
        <div className="settings-content">
          {/* Personal Profile Section */}
          <div className="settings-section">
            <h2>Personal Profile</h2>
            <div className="settings-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={profileForm.first_name}
                    onChange={(e) => handleProfileChange('first_name', e.target.value)}
                    placeholder="First name"
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={profileForm.last_name}
                    onChange={(e) => handleProfileChange('last_name', e.target.value)}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={profileForm.bio}
                  onChange={(e) => handleProfileChange('bio', e.target.value)}
                  placeholder="Tell us about yourself..."
                  rows={4}
                />
              </div>

              {renderImageUpload('profile-img', profileForm.profile_img, 'Profile Image', true)}
            </div>
          </div>

          {/* Artist Profile Section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <h2>
                <Palette size={20} />
                Artist Profile
              </h2>
              <span className="section-note">Used in VibrantCast mobile app</span>
            </div>
            <div className="settings-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Artist Handle</label>
                  <input
                    type="text"
                    value={profileForm.handle}
                    onChange={(e) => handleProfileChange('handle', e.target.value)}
                    placeholder="your-artist-handle"
                  />
                  <span className="field-note">Used in your artist profile URL</span>
                </div>

                <div className="form-group">
                  <label>Instagram</label>
                  <input
                    type="text"
                    value={profileForm.instagram}
                    onChange={(e) => handleProfileChange('instagram', e.target.value)}
                    placeholder="@yourusername"
                  />
                </div>
              </div>

              {renderImageUpload('profile-cover', profileForm.cover_image, 'Cover Image', false)}
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="settings-section">
            <h2>Contact Information</h2>
            <div className="settings-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => handleProfileChange('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>

                <div className="form-group">
                  <label>Mobile Phone</label>
                  <input
                    type="tel"
                    value={profileForm.mobile_phone}
                    onChange={(e) => handleProfileChange('mobile_phone', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Account Email (read-only) */}
          <div className="settings-section">
            <h2>Account</h2>
            <div className="settings-form">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="readonly"
                />
                <span className="field-note">Contact support to change your email address</span>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="settings-actions">
            <button 
              className="btn-primary"
              onClick={handleSaveProfile}
              disabled={saving}
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save size={18} />
                  Save Profile
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorSettings;
